import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, ArrowLeft, ChevronDown, ChevronRight, ExternalLink, FileText, Mail, Paperclip, X } from 'lucide-react';
import { caseDbApiEndpoint } from '../utils/apiOrigin';
import { ensureCaseDbCsrfToken, withCsrfHeaders } from '../utils/csrf';
import '../styles/OutlookCitationReviewer.css';

const HOVER_OPEN_DELAY_MS = 450;
const HOVER_CLOSE_DELAY_MS = 220;
const MAX_THREAD_MESSAGES = 20;
const MAX_BODY_CHARACTERS = 100000;
const MAX_PDF_PREVIEW_BYTES = 64 * 1024 * 1024;

const cleanText = (value, maxLength = 1000) => {
    const normalized = typeof value === 'string' || typeof value === 'number'
        ? String(value).replace(/\u0000/g, '').trim()
        : '';
    return normalized.length > maxLength ? `${normalized.slice(0, maxLength).trimEnd()}…` : normalized;
};

const normalizeParty = (party) => {
    if (typeof party === 'string') return cleanText(party, 500);
    if (!party || typeof party !== 'object') return '';
    const mailbox = party.emailAddress && typeof party.emailAddress === 'object' ? party.emailAddress : party;
    const name = cleanText(mailbox.name || mailbox.display_name || mailbox.displayName, 200);
    const address = cleanText(mailbox.address || mailbox.email || mailbox.email_address, 320);
    if (name && address && name.toLowerCase() !== address.toLowerCase()) return `${name} <${address}>`;
    return name || address;
};

const normalizePartyList = (value) => {
    const values = Array.isArray(value) ? value : (value ? [value] : []);
    return values.map(normalizeParty).filter(Boolean).slice(0, 100);
};

const compactWhitespace = (value, maxLength = 1000) => cleanText(value, maxLength)
    .replace(/\s+/g, ' ')
    .trim();

const firstTextValue = (...values) => values
    .map(value => compactWhitespace(value, 1000))
    .find(Boolean) || '';

export const hasOutlookRawPreviewLabel = (value = '') => (
    /\b(?:preview(?:\s+for)?|body(?:\s+preview)?)\s*:/i.test(String(value || ''))
);

const normalizeDirection = (value) => {
    const direction = cleanText(value, 30).toLowerCase();
    if (['inbound', 'incoming', 'received'].includes(direction)) return 'Received';
    if (['outbound', 'outgoing', 'sent'].includes(direction)) return 'Sent';
    return '';
};

const normalizeOutlookWebLink = (value) => {
    const raw = cleanText(value, 4000);
    if (!raw || /[\u0000-\u001f\u007f]/.test(raw)) return '';
    try {
        const url = new URL(raw);
        const hostname = url.hostname.toLowerCase();
        const allowedHost = ['outlook.office.com', 'outlook.office365.com', 'outlook.live.com'].includes(hostname)
            || hostname.endsWith('.outlook.office.com')
            || hostname.endsWith('.outlook.office365.com');
        if (
            url.protocol !== 'https:'
            || url.username
            || url.password
            || url.hash
            || !allowedHost
        ) {
            return '';
        }
        return url.toString();
    } catch {
        return '';
    }
};

export const getOutlookActionWebLink = (value, action = 'view') => {
    const normalized = normalizeOutlookWebLink(value);
    if (!normalized) return '';
    try {
        const url = new URL(normalized);
        url.searchParams.set('ispopout', action === 'respond' ? '1' : '0');
        return normalizeOutlookWebLink(url.toString());
    } catch {
        return '';
    }
};

const normalizeOutlookMessage = (message = {}, isCitedFallback = false) => {
    const rawBody = typeof message.body_text === 'string' ? message.body_text.replace(/\u0000/g, '') : '';
    const bodyWasClientTruncated = rawBody.length > MAX_BODY_CHARACTERS;
    return {
        subject: cleanText(message.subject, 500) || '(No subject)',
        from: normalizeParty(message.from),
        to: normalizePartyList(message.to),
        cc: normalizePartyList(message.cc),
        receivedAt: cleanText(message.received_at, 100),
        sentAt: cleanText(message.sent_at, 100),
        direction: normalizeDirection(message.direction),
        bodyText: rawBody.slice(0, MAX_BODY_CHARACTERS),
        bodyTruncated: Boolean(message.body_truncated || bodyWasClientTruncated),
        hasAttachments: Boolean(message.has_attachments || Number(message.attachment_count || 0) > 0),
        attachmentCount: Math.max(0, Math.min(1000, Number(message.attachment_count || 0) || 0)),
        isCited: Boolean(message.is_cited || isCitedFallback),
    };
};

const getMessageTime = (message) => {
    const timestamp = Date.parse(message.receivedAt || message.sentAt || '');
    return Number.isFinite(timestamp) ? timestamp : Number.MAX_SAFE_INTEGER;
};

export const normalizeOutlookCitationPreview = (preview = {}) => {
    const citedMessage = normalizeOutlookMessage(preview, true);
    const rawThreadMessages = Array.isArray(preview?.thread?.messages) ? preview.thread.messages : [];
    const normalizedThreadMessages = rawThreadMessages
        .slice(0, 100)
        .map(message => normalizeOutlookMessage(message, false))
        .sort((left, right) => getMessageTime(left) - getMessageTime(right));

    if (!normalizedThreadMessages.some(message => message.isCited)) {
        normalizedThreadMessages.push(citedMessage);
        normalizedThreadMessages.sort((left, right) => getMessageTime(left) - getMessageTime(right));
    }

    let messages = normalizedThreadMessages.slice(0, MAX_THREAD_MESSAGES);
    const citedMessageOutsideBound = normalizedThreadMessages.find(message => message.isCited && !messages.includes(message));
    if (citedMessageOutsideBound) {
        messages = [...messages.slice(0, MAX_THREAD_MESSAGES - 1), citedMessageOutsideBound]
            .sort((left, right) => getMessageTime(left) - getMessageTime(right));
    }

    const reportedCount = Number(preview?.thread?.message_count || 0);
    const messageCount = Number.isFinite(reportedCount) && reportedCount > 0
        ? Math.min(1000000, Math.floor(reportedCount))
        : normalizedThreadMessages.length;

    return {
        ...citedMessage,
        webLink: normalizeOutlookWebLink(preview.web_link),
        thread: {
            messages,
            messageCount,
            truncated: Boolean(preview?.thread?.truncated || normalizedThreadMessages.length > MAX_THREAD_MESSAGES || messageCount > messages.length),
        },
    };
};

export const isOutlookCitation = (citation = {}) => {
    if (!citation || (typeof citation !== 'object' && typeof citation !== 'function')) return false;

    let marker = '';
    try {
        marker = [citation.source_type, citation.type, citation.chunk_source, citation.provider]
            .map(value => cleanText(value, 100).toLowerCase())
            .join(' ');
    } catch {
        // A stale/revoked state proxy or throwing accessor must not crash the Jane surface.
        return false;
    }
    return marker.includes('outlook') || /(?:^|\s)(?:email|mail)(?:\s|$)/.test(marker);
};

export const getOutlookCitationReviewTarget = (citation = {}, context = {}) => {
    if (!isOutlookCitation(citation)) return null;
    const embeddedContext = citation?.__outlook_review_context && typeof citation?.__outlook_review_context === 'object'
        ? citation?.__outlook_review_context
        : {};
    const chatId = Number(context?.chatId || context?.chat_id || embeddedContext?.chatId || embeddedContext?.chat_id || 0);
    const assistantMessageId = Number(
        context.assistantMessageId
        || context.assistant_message_id
        || embeddedContext.assistantMessageId
        || embeddedContext.assistant_message_id
        || 0
    );
    const sourceId = cleanText(citation.source_id, 500);
    const citationHandle = cleanText(citation.citation_handle, 4000);
    if (!Number.isFinite(chatId) || chatId <= 0 || !Number.isFinite(assistantMessageId) || assistantMessageId <= 0 || !sourceId || !citationHandle) {
        return null;
    }
    return {
        chatId: Math.floor(chatId),
        assistantMessageId: Math.floor(assistantMessageId),
        sourceId,
        citationHandle,
        citation: {
            source_type: cleanText(citation.source_type || citation.type, 100),
            title: cleanText(citation.title, 500),
            label: cleanText(citation.label, 500),
            file_name: cleanText(citation.file_name, 500),
        },
        cacheKey: `${Math.floor(chatId)}:${Math.floor(assistantMessageId)}:${sourceId}:${citationHandle}`,
    };
};

export const withOutlookCitationReviewContext = (citation = {}, context = {}) => {
    if (!isOutlookCitation(citation)) return citation;
    return {
        ...citation,
        __outlook_review_context: {
            chatId: Number(context.chatId || context.chat_id || 0) || null,
            assistantMessageId: Number(context.assistantMessageId || context.assistant_message_id || 0) || null,
        },
    };
};

const formatReviewerTimestamp = (value) => {
    if (!value) return '';
    const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)
        ? `${value.replace(' ', 'T')}Z`
        : value;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return cleanText(value, 100);
    return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    }).format(date);
};

const parseLegacyOutlookExcerpt = (excerpt) => {
    const parts = cleanText(excerpt, 1000)
        .split(/\s+·\s+/)
        .map(part => part.trim())
        .filter(Boolean);
    if (parts.length < 2) return { sender: '', timestamp: '' };
    const timestamp = parts[parts.length - 1];
    if (!Number.isFinite(Date.parse(timestamp))) return { sender: '', timestamp: '' };
    return {
        sender: parts.slice(0, -1).join(' · '),
        timestamp,
    };
};

const normalizePositiveRecordId = (value) => {
    const raw = String(value ?? '').trim();
    if (!/^[1-9]\d*$/.test(raw)) return null;
    const parsed = Number(raw);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
};

export const getOutlookEmailCaseMatchModel = (citation = {}) => {
    const requestedStatus = cleanText(citation.case_match_status || citation.caseMatchStatus, 30).toLowerCase();
    if (!['unique', 'ambiguous'].includes(requestedStatus)) {
        return { status: '', matches: [] };
    }

    const seen = new Set();
    const matches = (Array.isArray(citation.case_matches) ? citation.case_matches : [])
        .reduce((normalized, rawMatch) => {
            if (!rawMatch || typeof rawMatch !== 'object') return normalized;
            const caseId = normalizePositiveRecordId(rawMatch.case_id || rawMatch.caseId);
            if (!caseId) return normalized;
            const matchBasis = cleanText(rawMatch.match_basis || rawMatch.matchBasis, 40).toLowerCase();
            if (!['primary_contact_email', 'case_unique_email'].includes(matchBasis)) return normalized;
            const contactId = matchBasis === 'primary_contact_email'
                ? normalizePositiveRecordId(rawMatch.contact_id || rawMatch.contactId)
                : null;
            const identity = `${caseId}:${contactId || 0}:${matchBasis}`;
            if (seen.has(identity)) return normalized;
            seen.add(identity);
            normalized.push({
                caseId,
                caseName: cleanText(rawMatch.case_name || rawMatch.caseName, 300) || `Case ${caseId}`,
                casePath: `/case/${caseId}`,
                contactId,
                contactName: contactId
                    ? (cleanText(rawMatch.contact_name || rawMatch.contactName, 300) || `Contact ${contactId}`)
                    : '',
                contactPath: contactId ? `/contact/${contactId}` : '',
                matchBasis,
            });
            return normalized;
        }, []);

    if (matches.length === 0) return { status: '', matches: [] };
    return {
        status: requestedStatus === 'unique' && matches.length === 1 ? 'unique' : 'ambiguous',
        matches,
    };
};

export const getOutlookEmailCardModel = (citation = {}, { truncateSummary = true } = {}) => {
    const legacyExcerpt = parseLegacyOutlookExcerpt(citation.excerpt);
    const sender = firstTextValue(
        citation.sender_label,
        citation.senderLabel,
        normalizeParty(citation.sender),
        normalizeParty(citation.from),
        legacyExcerpt.sender
    );
    const recipients = normalizePartyList(
        citation.recipient_labels
        || citation.recipientLabels
        || citation.recipients
        || citation.to
    );
    const timestamp = firstTextValue(
        citation.received_at,
        citation.receivedAt,
        citation.sent_at,
        citation.sentAt,
        legacyExcerpt.timestamp
    );
    const rawSummary = [
        citation.summary,
        citation.email_summary,
        citation.emailSummary,
        citation.concise_summary,
        citation.conciseSummary,
        citation.preview_summary,
        citation.previewSummary
    ].map(value => compactWhitespace(value, truncateSummary ? 1000 : Infinity)).find(Boolean) || '';
    const summary = hasOutlookRawPreviewLabel(rawSummary)
        ? ''
        : cleanText(rawSummary.replace(/^summary\s*:\s*/i, ''), truncateSummary ? 420 : Infinity);
    const attachmentCount = Math.max(0, Math.min(1000, Number(citation.attachment_count || citation.attachmentCount || 0) || 0));
    const caseMatchModel = getOutlookEmailCaseMatchModel(citation);

    return {
        subject: firstTextValue(citation.title, citation.subject, citation.label) || '(No subject)',
        sender,
        recipients,
        timestamp,
        direction: normalizeDirection(citation.direction),
        summary,
        hasAttachments: Boolean(citation.has_attachments || citation.hasAttachments || attachmentCount > 0),
        attachmentCount,
        caseMatchStatus: caseMatchModel.status,
        caseMatches: caseMatchModel.matches,
    };
};

export const OutlookEmailCaseMatchLinks = ({ citation = {}, compact = false }) => {
    const { status, matches } = getOutlookEmailCaseMatchModel(citation);
    if (matches.length === 0) return null;

    return (
        <div
            className={`library-rag-outlook-email-case-matches${compact ? ' compact' : ''}${status === 'ambiguous' ? ' ambiguous' : ''}`}
            aria-label={status === 'ambiguous' ? 'Possible CaseDB matches' : 'CaseDB match'}
        >
            {status === 'ambiguous' ? (
                <span className="library-rag-outlook-email-case-match-label">Needs case review:</span>
            ) : null}
            <span className="library-rag-outlook-email-case-match-links">
                {matches.map((match, matchIndex) => (
                    <span className="library-rag-outlook-email-case-match" key={`${match.caseId}-${match.contactId || 0}-${match.matchBasis}`}>
                        {matchIndex > 0 ? <span aria-hidden="true"> · </span> : null}
                        <a href={match.casePath} target="_blank" rel="noopener noreferrer">{match.caseName}</a>
                        {match.contactId ? (
                            <>
                                <span aria-hidden="true"> / </span>
                                <a href={match.contactPath} target="_blank" rel="noopener noreferrer">{match.contactName}</a>
                            </>
                        ) : null}
                    </span>
                ))}
            </span>
        </div>
    );
};

export const OutlookEmailCitationCard = ({
    citation = {},
    sourceNumber = null,
    chatId = null,
    assistantMessageId = null,
    truncateSummary = true,
}) => {
    const model = getOutlookEmailCardModel(citation, { truncateSummary });
    const reviewTarget = getOutlookCitationReviewTarget(citation, { chatId, assistantMessageId });
    const formattedTimestamp = formatReviewerTimestamp(model.timestamp);
    const recipientText = model.recipients.join('; ');
    const counterparty = model.direction === 'Sent' && recipientText
        ? `To ${recipientText}`
        : (model.sender ? `From ${model.sender}` : (recipientText ? `To ${recipientText}` : ''));
    const attachmentText = model.attachmentCount > 0
        ? `${model.attachmentCount} attachment${model.attachmentCount === 1 ? '' : 's'}`
        : 'Attachments';

    return (
        <article className={`library-rag-outlook-email-card${truncateSummary ? '' : ' full-summary'}`} aria-label={`Outlook email: ${model.subject}`}>
            <div className="library-rag-outlook-email-card-content">
                <div className="library-rag-outlook-email-card-scanline">
                    {sourceNumber !== null && sourceNumber !== undefined && sourceNumber !== '' && (
                        <span className="library-rag-outlook-email-card-number">[{sourceNumber}]</span>
                    )}
                    <h4 title={model.subject}>{model.subject}</h4>
                    {counterparty && <span className="library-rag-outlook-email-card-counterparty" title={counterparty}>{counterparty}</span>}
                    {formattedTimestamp && <time dateTime={model.timestamp}>{formattedTimestamp}</time>}
                    {model.hasAttachments ? (
                        <span className="library-rag-outlook-email-card-attachments" title={attachmentText} aria-label={attachmentText}>
                            <Paperclip size={12} />
                        </span>
                    ) : <span className="library-rag-outlook-email-card-attachments-placeholder" />}
                    {reviewTarget ? (
                        <OutlookEmailActionsMenu
                            target={reviewTarget}
                            subject={model.subject}
                        />
                    ) : null}
                </div>
                {model.summary && (
                    <p className="library-rag-outlook-email-card-summary">{model.summary}</p>
                )}
                <OutlookEmailCaseMatchLinks citation={citation} />
            </div>
        </article>
    );
};

const getPreviewErrorMessage = (status) => {
    if (status === 401) return 'Your CaseDB session expired. Refresh the page and try again.';
    if (status === 403) return 'This cited email is not available to your account.';
    if (status === 404 || status === 410) return 'This cited email preview is no longer available.';
    return 'The cited email preview could not be loaded.';
};

const loadOutlookCitationPreview = async (target, signal) => {
    await ensureCaseDbCsrfToken();
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    const response = await fetch(caseDbApiEndpoint('jane-outlook-citation-preview.php'), {
        method: 'POST',
        credentials: 'include',
        signal,
        headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
        body: JSON.stringify({
            chat_id: target.chatId,
            assistant_message_id: target.assistantMessageId,
            source_id: target.sourceId,
            citation_handle: target.citationHandle,
        }),
    });
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    const data = await response.json().catch(() => ({}));
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    const preview = data?.preview || data?.reviewer;
    if (!response.ok || data?.success === false || !preview || typeof preview !== 'object') {
        const error = new Error(getPreviewErrorMessage(response.status));
        error.status = response.status;
        throw error;
    }
    return normalizeOutlookCitationPreview(preview);
};

const loadOutlookCitationPdf = async (target, signal) => {
    await ensureCaseDbCsrfToken();
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    const response = await fetch(caseDbApiEndpoint('jane-outlook-citation-preview.php'), {
        method: 'POST',
        credentials: 'include',
        signal,
        headers: withCsrfHeaders({
            'Content-Type': 'application/json',
            Accept: 'application/pdf',
        }, 'POST'),
        body: JSON.stringify({
            chat_id: target.chatId,
            assistant_message_id: target.assistantMessageId,
            source_id: target.sourceId,
            citation_handle: target.citationHandle,
            view: 'pdf',
        }),
    });
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    if (!response.ok) {
        const error = new Error(getPreviewErrorMessage(response.status));
        error.status = response.status;
        throw error;
    }
    const contentType = String(response.headers?.get?.('Content-Type') || '').toLowerCase();
    const contentLength = Number(response.headers?.get?.('Content-Length') || 0);
    if ((contentType && !contentType.includes('application/pdf')) || (Number.isFinite(contentLength) && contentLength > MAX_PDF_PREVIEW_BYTES)) {
        throw new Error('The cited email PDF preview could not be loaded.');
    }
    const blob = await response.blob();
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    if (!blob || blob.size <= 0 || blob.size > MAX_PDF_PREVIEW_BYTES) {
        throw new Error('The cited email PDF preview could not be loaded.');
    }
    return blob.type === 'application/pdf' ? blob : blob.slice(0, blob.size, 'application/pdf');
};

// Bind a request to the actual endpoint arguments, not the caller's object
// identity. This is not a cache: closing and reopening creates a new lifetime.
const getOutlookPreviewRequestIdentity = (target) => target?.cacheKey
    ? JSON.stringify([target.chatId, target.assistantMessageId, target.sourceId, target.citationHandle])
    : '';

const useOutlookPreviewRequest = (target, enabled) => {
    const identity = getOutlookPreviewRequestIdentity(target);
    return useMemo(() => {
        if (!enabled || !identity) return null;
        const [chatId, assistantMessageId, sourceId, citationHandle] = JSON.parse(identity);
        return { target: { chatId, assistantMessageId, sourceId, citationHandle } };
    }, [enabled, identity]);
};

const useOutlookCitationPreview = (target, enabled = true) => {
    const request = useOutlookPreviewRequest(target, enabled);
    const [state, setState] = useState({ request: null, preview: null, error: '' });

    useEffect(() => {
        if (!request) {
            setState({ request: null, preview: null, error: '' });
            return undefined;
        }
        let active = true;
        const controller = new AbortController();
        setState({ request, loading: true, preview: null, error: '' });
        loadOutlookCitationPreview(request.target, controller.signal)
            .then((preview) => {
                if (!active || controller.signal.aborted) return;
                setState({ request, loading: false, preview, error: '' });
            })
            .catch((error) => {
                if (!active || controller.signal.aborted || error?.name === 'AbortError') return;
                setState({ request, loading: false, preview: null, error: error?.message || getPreviewErrorMessage(0) });
            });
        return () => {
            active = false;
            controller.abort();
        };
    }, [request]);

    // A render for a closed/new target must never expose the preceding body,
    // even before passive cleanup or the new request's effect has run.
    return request && state.request === request
        ? state
        : { loading: Boolean(request), preview: null, error: '' };
};

const OutlookEmailActionsMenu = ({ target, subject }) => {
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState({ top: -10000, left: -10000 });
    const { loading, preview, error } = useOutlookCitationPreview(target, open);
    const viewLink = getOutlookActionWebLink(preview?.webLink, 'view');
    const respondLink = getOutlookActionWebLink(preview?.webLink, 'respond');
    const pending = loading || (!preview && !error);

    const updatePosition = useCallback(() => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const padding = 8;
        const menuWidth = Math.min(178, Math.max(0, window.innerWidth - (padding * 2)));
        const left = Math.max(padding, Math.min(
            window.innerWidth - menuWidth - padding,
            rect.right - menuWidth
        ));
        const estimatedHeight = 92;
        const top = rect.bottom + estimatedHeight + padding <= window.innerHeight
            ? rect.bottom + 5
            : Math.max(padding, rect.top - estimatedHeight - 5);
        setPosition({ top, left });
    }, []);

    useLayoutEffect(() => {
        if (!open) return undefined;
        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [open, updatePosition]);

    useEffect(() => {
        if (!open) return undefined;
        const closeForOutsidePointer = (event) => {
            if (triggerRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
            setOpen(false);
        };
        const closeForEscape = (event) => {
            if (event.key !== 'Escape') return;
            setOpen(false);
            triggerRef.current?.focus();
        };
        document.addEventListener('pointerdown', closeForOutsidePointer);
        document.addEventListener('keydown', closeForEscape);
        return () => {
            document.removeEventListener('pointerdown', closeForOutsidePointer);
            document.removeEventListener('keydown', closeForEscape);
        };
    }, [open]);

    useLayoutEffect(() => {
        if (!open || !menuRef.current) return;
        const firstItem = menuRef.current.querySelector('[role="menuitem"]');
        (firstItem || menuRef.current).focus();
    }, [error, open, pending, respondLink, viewLink]);

    const closeMenu = () => setOpen(false);
    const handleMenuKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            setOpen(false);
            triggerRef.current?.focus();
            return;
        }
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        const items = Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]') || []);
        if (items.length === 0) return;
        event.preventDefault();
        const currentIndex = items.indexOf(document.activeElement);
        let nextIndex = 0;
        if (event.key === 'End') nextIndex = items.length - 1;
        if (event.key === 'ArrowDown') nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % items.length;
        if (event.key === 'ArrowUp') nextIndex = currentIndex < 0 ? items.length - 1 : (currentIndex - 1 + items.length) % items.length;
        items[nextIndex]?.focus();
    };
    const menu = open ? createPortal(
        <div
            ref={menuRef}
            className="library-rag-outlook-email-actions-menu"
            style={position}
            role="menu"
            tabIndex={-1}
            aria-busy={pending}
            aria-label={`Email actions for ${subject}`}
            onKeyDown={handleMenuKeyDown}
        >
            {pending ? (
                <span className="library-rag-outlook-email-actions-state" role="status">Opening Outlook…</span>
            ) : viewLink && respondLink ? (
                <>
                    <a href={viewLink} target="_blank" rel="noopener noreferrer" role="menuitem" onClick={closeMenu}>
                        <ExternalLink size={15} aria-hidden="true" />
                        <span>View email</span>
                    </a>
                    <a
                        href={respondLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        role="menuitem"
                        title="Open the message in Outlook to respond"
                        onClick={closeMenu}
                    >
                        <ArrowLeft size={15} aria-hidden="true" />
                        <span>Respond</span>
                    </a>
                </>
            ) : (
                <span className="library-rag-outlook-email-actions-state error" role="alert">
                    {error || 'Outlook link unavailable.'}
                </span>
            )}
        </div>,
        document.body
    ) : null;

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className="action-micro tertiary library-rag-outlook-email-actions-trigger"
                aria-label={`Email actions for ${subject}`}
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setOpen(current => !current);
                }}
            >
                <span className="library-rag-outlook-email-actions-dots" aria-hidden="true">…</span>
            </button>
            {menu}
        </>
    );
};

const useOutlookCitationPdf = (target, enabled = true) => {
    const request = useOutlookPreviewRequest(target, enabled);
    const [state, setState] = useState({ request: null, url: '', error: '' });

    useEffect(() => {
        if (!request) {
            setState({ request: null, url: '', error: '' });
            return undefined;
        }
        let active = true;
        const controller = new AbortController();
        let objectUrl = '';
        setState({ request, loading: true, url: '', error: '' });
        loadOutlookCitationPdf(request.target, controller.signal)
            .then((blob) => {
                if (!active || controller.signal.aborted) return;
                objectUrl = window.URL.createObjectURL(blob);
                setState({ request, loading: false, url: objectUrl, error: '' });
            })
            .catch((error) => {
                if (!active || controller.signal.aborted || error?.name === 'AbortError') return;
                setState({ request, loading: false, url: '', error: error?.message || getPreviewErrorMessage(0) });
            });
        return () => {
            active = false;
            controller.abort();
            if (objectUrl) window.URL.revokeObjectURL(objectUrl);
        };
    }, [request]);

    return request && state.request === request
        ? state
        : { loading: Boolean(request), url: '', error: '' };
};

const AddressRow = ({ label, values }) => {
    if (!Array.isArray(values) || values.length === 0) return null;
    return (
        <div className="outlook-citation-reviewer-address-row">
            <span>{label}</span>
            <div>{values.join('; ')}</div>
        </div>
    );
};

const MessageIndicators = ({ message }) => (
    <span className="outlook-citation-reviewer-indicators">
        {message.hasAttachments && (
            <span className="outlook-citation-reviewer-indicator">
                <Paperclip size={12} />
                {message.attachmentCount > 0 ? `${message.attachmentCount} attachment${message.attachmentCount === 1 ? '' : 's'}` : 'Attachments'}
            </span>
        )}
        {message.bodyTruncated && <span className="outlook-citation-reviewer-indicator warning">Body truncated</span>}
    </span>
);

const OutlookThreadMessage = ({ message, index, expanded, onToggle }) => {
    const timestamp = formatReviewerTimestamp(message.receivedAt || message.sentAt);
    return (
        <article className={`outlook-citation-thread-message${message.isCited ? ' cited' : ''}`}>
            <button
                type="button"
                className="outlook-citation-thread-summary"
                onClick={() => onToggle(index)}
                aria-expanded={expanded}
            >
                {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                <span className="outlook-citation-thread-summary-copy">
                    <span className="outlook-citation-thread-summary-topline">
                        <strong>{message.from || 'Unknown sender'}</strong>
                        <time>{timestamp}</time>
                    </span>
                    <span className="outlook-citation-thread-subject">{message.subject}</span>
                    <span className="outlook-citation-thread-summary-meta">
                        {message.isCited && <span className="outlook-citation-cited-badge">Cited email</span>}
                        {message.direction && <span>{message.direction}</span>}
                        <MessageIndicators message={message} />
                    </span>
                </span>
            </button>
            {expanded && (
                <div className="outlook-citation-thread-detail">
                    <div className="outlook-citation-reviewer-addresses">
                        {message.from && <AddressRow label="From" values={[message.from]} />}
                        <AddressRow label="To" values={message.to} />
                        <AddressRow label="Cc" values={message.cc} />
                        {message.sentAt && <AddressRow label="Sent" values={[formatReviewerTimestamp(message.sentAt)]} />}
                        {message.receivedAt && <AddressRow label="Received" values={[formatReviewerTimestamp(message.receivedAt)]} />}
                    </div>
                    <pre className="outlook-citation-reviewer-body-text">
                        {message.bodyText || 'No plain-text body is available for this message.'}
                    </pre>
                </div>
            )}
        </article>
    );
};

const OutlookCitationReviewerContent = ({ preview }) => {
    const messages = Array.isArray(preview?.thread?.messages) ? preview.thread.messages : [];
    const initialExpanded = useMemo(() => new Set(
        messages.map((message, index) => message.isCited ? index : null).filter(index => index !== null)
    ), [messages]);
    const [expandedMessages, setExpandedMessages] = useState(initialExpanded);

    useEffect(() => setExpandedMessages(initialExpanded), [initialExpanded]);

    const toggleMessage = (index) => {
        setExpandedMessages(current => {
            const next = new Set(current);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    };

    return (
        <div className="outlook-citation-reviewer-content">
            <div className="outlook-citation-reviewer-thread-heading">
                <span>{preview.thread.messageCount} message{preview.thread.messageCount === 1 ? '' : 's'} · chronological</span>
                {preview.thread.truncated && <span className="outlook-citation-reviewer-thread-truncated">Thread truncated to {messages.length}</span>}
            </div>
            <div className="outlook-citation-reviewer-thread">
                {messages.map((message, index) => (
                    <OutlookThreadMessage
                        key={`${message.sentAt || message.receivedAt || 'message'}-${index}`}
                        message={message}
                        index={index}
                        expanded={expandedMessages.has(index)}
                        onToggle={toggleMessage}
                    />
                ))}
            </div>
        </div>
    );
};

export const OutlookCitationReviewerPanel = ({ target, citation, chatId, assistantMessageId, variant = 'rail', onClose = null }) => {
    const resolvedTarget = useMemo(
        () => target || getOutlookCitationReviewTarget(citation, { chatId, assistantMessageId }),
        [assistantMessageId, chatId, citation, target]
    );
    const preferredView = variant === 'rail' && resolvedTarget?.initialView === 'pdf' ? 'pdf' : 'thread';
    const requestIdentity = getOutlookPreviewRequestIdentity(resolvedTarget);
    const [viewSelection, setViewSelection] = useState({ requestIdentity, preferredView, view: preferredView });
    const selectionIsCurrent = viewSelection.requestIdentity === requestIdentity && viewSelection.preferredView === preferredView;
    const view = selectionIsCurrent ? viewSelection.view : preferredView;
    useEffect(() => {
        setViewSelection(current => current.requestIdentity === requestIdentity && current.preferredView === preferredView
            ? current
            : { requestIdentity, preferredView, view: preferredView });
    }, [preferredView, requestIdentity]);
    const {
        loading: threadLoading,
        preview,
        error: threadError,
    } = useOutlookCitationPreview(resolvedTarget, Boolean(resolvedTarget) && view === 'thread');
    const {
        loading: pdfLoading,
        url: pdfUrl,
        error: pdfError,
    } = useOutlookCitationPdf(resolvedTarget, Boolean(resolvedTarget) && view === 'pdf');
    const loading = view === 'pdf' ? pdfLoading : threadLoading;
    const error = view === 'pdf' ? pdfError : threadError;
    const title = preview?.subject || cleanText(resolvedTarget?.citation?.title || resolvedTarget?.citation?.label, 500) || 'Cited Outlook email';
    const meta = preview
        ? [preview.from, formatReviewerTimestamp(preview.receivedAt || preview.sentAt)].filter(Boolean).join(' · ')
        : (view === 'pdf' ? 'Rendered from the live cited message' : 'Live mailbox review');

    return (
        <section
            className={`outlook-citation-reviewer outlook-citation-reviewer-${variant}`}
            aria-label="Cited Outlook email reviewer"
            aria-live="polite"
            aria-busy={loading}
        >
            <header className="outlook-citation-reviewer-header">
                <span className="outlook-citation-reviewer-icon"><Mail size={18} /></span>
                <span className="outlook-citation-reviewer-heading">
                    <span>Cited Outlook email</span>
                    <strong title={title}>{title}</strong>
                    <small>{meta}</small>
                </span>
                <span className="outlook-citation-reviewer-header-actions">
                    {variant === 'rail' && resolvedTarget && (
                        <button
                            type="button"
                            className="outlook-citation-reviewer-view-toggle"
                            onClick={() => setViewSelection({ requestIdentity, preferredView, view: view === 'pdf' ? 'thread' : 'pdf' })}
                            aria-label={view === 'pdf' ? 'Back to email thread' : 'See cited email as PDF'}
                            title={view === 'pdf' ? 'Back to thread' : 'See email'}
                        >
                            {view === 'pdf' ? <ArrowLeft size={14} /> : <FileText size={14} />}
                            <span>{view === 'pdf' ? 'Thread' : 'See email'}</span>
                        </button>
                    )}
                    {preview?.webLink && (
                        <a
                            href={preview.webLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Open cited email in Outlook"
                            title="Open in Outlook"
                        >
                            <ExternalLink size={15} />
                        </a>
                    )}
                    {onClose && (
                        <button type="button" onClick={onClose} aria-label="Close cited email reviewer" title="Close reviewer">
                            <X size={16} />
                        </button>
                    )}
                </span>
            </header>
            {loading && <div className="outlook-citation-reviewer-state">Loading the cited email…</div>}
            {!loading && error && (
                <div className="outlook-citation-reviewer-state error">
                    <AlertCircle size={17} />
                    <span>{error}</span>
                </div>
            )}
            {view === 'pdf' && !loading && !error && pdfUrl && (
                <div className="outlook-citation-reviewer-pdf">
                    <iframe src={pdfUrl} title={`Email preview: ${title}`} />
                </div>
            )}
            {view === 'thread' && !loading && !error && preview && <OutlookCitationReviewerContent preview={preview} />}
        </section>
    );
};

export const OutlookCitationReviewTrigger = ({
    citation,
    chatId,
    assistantMessageId,
    children,
    as: Trigger = 'button',
    className = '',
    openDelay = HOVER_OPEN_DELAY_MS,
    onActivate = null,
    ...triggerProps
}) => {
    const target = useMemo(
        () => getOutlookCitationReviewTarget(citation, { chatId, assistantMessageId }),
        [assistantMessageId, chatId, citation]
    );
    const triggerRef = useRef(null);
    const previewRef = useRef(null);
    const openTimerRef = useRef(null);
    const closeTimerRef = useRef(null);
    const pointerRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState({ top: -10000, left: -10000, width: 640, maxHeight: 620, placement: 'below' });

    const clearTimers = useCallback(() => {
        if (openTimerRef.current) window.clearTimeout(openTimerRef.current);
        if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
        openTimerRef.current = null;
        closeTimerRef.current = null;
    }, []);

    const showPreview = useCallback(() => {
        if (!target) return;
        if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
        if (open || openTimerRef.current) return;
        openTimerRef.current = window.setTimeout(() => {
            openTimerRef.current = null;
            setOpen(true);
        }, openDelay);
    }, [open, openDelay, target]);

    const hidePreview = useCallback(() => {
        if (openTimerRef.current) window.clearTimeout(openTimerRef.current);
        openTimerRef.current = null;
        if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = window.setTimeout(() => {
            closeTimerRef.current = null;
            setOpen(false);
        }, HOVER_CLOSE_DELAY_MS);
    }, []);

    const updatePosition = useCallback(() => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const padding = 12;
        const gap = 10;
        const width = Math.max(280, Math.min(640, window.innerWidth - (padding * 2)));
        const preferredHeight = Math.max(180, Math.min(620, window.innerHeight - (padding * 2)));
        const measuredHeight = Math.min(previewRef.current?.offsetHeight || preferredHeight, preferredHeight);
        const belowRoom = Math.max(0, window.innerHeight - padding - rect.bottom - gap);
        const aboveRoom = Math.max(0, rect.top - padding - gap);
        const placement = belowRoom >= preferredHeight || belowRoom >= aboveRoom ? 'below' : 'above';
        const availableHeight = placement === 'below' ? belowRoom : aboveRoom;
        const maxHeight = Math.max(140, Math.min(preferredHeight, availableHeight || preferredHeight));
        const pointerX = pointerRef.current?.x;
        const center = Number.isFinite(pointerX) ? pointerX : rect.left + (rect.width / 2);
        const left = Math.min(window.innerWidth - width - padding, Math.max(padding, center - (width / 2)));
        setPosition({
            top: placement === 'below' ? rect.bottom + gap : Math.max(padding, rect.top - gap - Math.min(measuredHeight, maxHeight)),
            left,
            width,
            maxHeight,
            placement,
        });
    }, []);

    useLayoutEffect(() => {
        if (!open) return undefined;
        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [open, updatePosition]);

    useEffect(() => () => clearTimers(), [clearTimers]);

    const activate = (event) => {
        triggerProps.onClick?.(event);
        if (!target || event.defaultPrevented) return;
        event.preventDefault();
        event.stopPropagation();
        clearTimers();
        setOpen(false);
        onActivate?.(target);
    };

    const isNativeButton = Trigger === 'button';
    const resolvedTriggerProps = isNativeButton ? { type: 'button', ...triggerProps } : triggerProps;

    return (
        <>
            <Trigger
                {...resolvedTriggerProps}
                ref={triggerRef}
                className={`outlook-citation-review-trigger ${className}`.trim()}
                onClick={activate}
                onKeyDown={(event) => {
                    triggerProps.onKeyDown?.(event);
                    if (event.defaultPrevented || isNativeButton || (event.key !== 'Enter' && event.key !== ' ')) return;
                    activate(event);
                }}
                onMouseEnter={(event) => {
                    triggerProps.onMouseEnter?.(event);
                    pointerRef.current = { x: event.clientX, y: event.clientY };
                    showPreview();
                }}
                onMouseMove={(event) => {
                    triggerProps.onMouseMove?.(event);
                    pointerRef.current = { x: event.clientX, y: event.clientY };
                }}
                onMouseLeave={(event) => {
                    triggerProps.onMouseLeave?.(event);
                    hidePreview();
                }}
                onFocusCapture={(event) => {
                    triggerProps.onFocusCapture?.(event);
                    showPreview();
                }}
                onBlurCapture={(event) => {
                    triggerProps.onBlurCapture?.(event);
                    if (!previewRef.current?.contains(event.relatedTarget)) hidePreview();
                }}
                aria-haspopup="dialog"
            >
                {children}
            </Trigger>
            {open && target && createPortal(
                <aside
                    ref={previewRef}
                    className={`outlook-citation-hover-preview placement-${position.placement}`}
                    style={{ top: position.top, left: position.left, width: position.width, maxHeight: position.maxHeight }}
                    role="dialog"
                    aria-label="Cited Outlook email preview"
                    onClick={event => event.stopPropagation()}
                    onMouseEnter={() => {
                        if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
                        closeTimerRef.current = null;
                    }}
                    onMouseLeave={hidePreview}
                >
                    <OutlookCitationReviewerPanel target={target} variant="hover" />
                </aside>,
                document.body
            )}
        </>
    );
};

export default OutlookCitationReviewerPanel;
